import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import MonthPicker from '~/components/MonthPicker';
import { HelpText, HelpTextLink } from '~/components/Text';
import useTiering from '~/hooks/use-tiering';
import { capitalize } from '~/lib/strings';
import DragAndDropUploadFile from './Elements/DragAndDropUploadFile';
import InputWrapper, { propTypes as InputWrapperPropTypes } from './Elements/InputWrapper';
import PhoneInputEl from './Elements/PhoneInput';
import TextInput from './Elements/TextInput';

const Input = React.forwardRef(function Input(
  { label, type, inverse = false, inline = false, noMargin = false, helpText, renderFunc, className, error, ...rest },
  ref,
) {
  let el;
  if (type === 'drag-and-drop-file-upload') {
    el = <DragAndDropUploadFile {...rest} />;
  } else if (type === 'month-picker') {
    el = <MonthPicker {...rest} />;
  } else if (type === 'phone-number') {
    el = <PhoneInputEl {...rest} />;
  } else {
    el = (
      <TextInput
        ref={ref}
        type={type}
        error={error}
        {...rest}
      />
    );
  }

  return (
    <InputWrapper
      label={label}
      inline={inline}
      noMargin={noMargin}
      inverse={inverse}
      helpText={helpText}
      className={className}
      error={error}
      type={type}
      {...rest}
    >
      {/* TODO: renderFunc is a hack until model is rewritten */}
      {renderFunc ? renderFunc(rest.value, el) : el}
    </InputWrapper>
  );
});

const HelpTextWrapper = styled.div`
  display: flex;
`;

const PreRender = React.forwardRef(function PreRender(
  { featureName, featureLink, featureShowCTA = true, ...props },
  ref,
) {
  try {
    const tiering = useTiering();

    function MissingFeatureLabel() {
      if (!featureShowCTA) return null;
      const tierRequired = tiering.getTierByFeature(featureName);
      if (!tierRequired) return <HelpText>Upgrade to access feature</HelpText>;
      return (
        <>
          <HelpText>Feature available on </HelpText>
          <HelpTextLink> {capitalize(tierRequired)} users</HelpTextLink>
        </>
      );
    }

    function setDisabledAndPlaceholder() {
      const hasFeature = tiering.hasFeature(featureName);
      if (!featureName || hasFeature) return {};

      return {
        disabled: true,
        placeholder: '',
        canSearch: false,
        helpText: (
          <a
            href={featureLink}
            target='_blank'
            rel='noopener'
          >
            <HelpTextWrapper>
              <MissingFeatureLabel />
            </HelpTextWrapper>
          </a>
        ),
      };
    }

    const changes = setDisabledAndPlaceholder();

    return (
      <Input
        {...props}
        {...changes}
        ref={ref}
      />
    );
  } catch (e) {
    const isFeatureProviderError = e.message === 'useTiering must be used within a FeatureProvider';
    if (!isFeatureProviderError) throw e;
    return (
      <Input
        {...props}
        ref={ref}
      />
    );
  }
});

const inputPropTypes = {
  ...InputWrapperPropTypes,
  renderFunc: PropTypes.func,
  featureLink: PropTypes.string,
  featureShowCTA: PropTypes.bool,
};

PreRender.propTypes = inputPropTypes;
Input.propTypes = inputPropTypes;

export default PreRender;
